import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, Send, HeartCrack, CloudLightning, Compass, Ear, Heart, Moon } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Background images
const BACKGROUND_IMAGES = {
  primary: "https://images.unsplash.com/photo-1515694346937-94d85e41e6f0?w=1920&q=80",
  rain: "https://images.unsplash.com/photo-1534274988757-a28bf1a57c17?w=1920&q=80",
  road: "https://images.unsplash.com/photo-1507400492013-162706c8c05e?w=1920&q=80",
  night: "https://images.unsplash.com/photo-1505506874110-6a7a69069a08?w=1920&q=80",
  love: "https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?w=1920&q=80",
};

// Mood configurations
const MOODS = {
  heavy: { id: "heavy", label: "Heart feels heavy", icon: HeartCrack, iconColor: "#e57373", bg: BACKGROUND_IMAGES.rain },
  restless: { id: "restless", label: "Soul feels restless", icon: CloudLightning, iconColor: "#81d4fa", bg: BACKGROUND_IMAGES.night },
  lost: { id: "lost", label: "Feeling lost", icon: Compass, iconColor: "#ffb74d", bg: BACKGROUND_IMAGES.road },
  heard: { id: "heard", label: "Want to be heard", icon: Ear, iconColor: "#a5d6a7", bg: BACKGROUND_IMAGES.primary },
  love: { id: "love", label: "In Love", icon: Heart, iconColor: "#f48fb1", bg: BACKGROUND_IMAGES.love },
};

// LocalStorage helpers
const STORAGE_KEYS = { USER_MEMORY: "sukoon_user_memory", SESSION_ID: "sukoon_session_id" };
const generateSessionId = () => `sukoon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const getSessionId = () => {
  let id = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
  if (!id) { id = generateSessionId(); localStorage.setItem(STORAGE_KEYS.SESSION_ID, id); }
  return id;
};
const getUserMemory = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEYS.USER_MEMORY)) || { name: null, details: [], lastMood: null, visitCount: 0 }; }
  catch { return { name: null, details: [], lastMood: null, visitCount: 0 }; }
};
const saveUserMemory = (m) => localStorage.setItem(STORAGE_KEYS.USER_MEMORY, JSON.stringify(m));

function App() {
  const [selectedMood, setSelectedMood] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [volume, setVolume] = useState(0.15);
  const [userMemory, setUserMemory] = useState(getUserMemory);
  const [sessionId] = useState(getSessionId);
  const [currentBg, setCurrentBg] = useState(BACKGROUND_IMAGES.primary);
  
  const audioRef = useRef(null);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const mem = getUserMemory();
    mem.visitCount = (mem.visitCount || 0) + 1;
    saveUserMemory(mem);
    setUserMemory(mem);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = isMuted ? 0 : volume;
  }, [volume, isMuted]);

  const handleMoodSelect = (mood) => {
    setSelectedMood(mood);
    setCurrentBg(mood.bg);
    const mem = { ...userMemory, lastMood: mood.id };
    saveUserMemory(mem);
    setUserMemory(mem);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    const userMessage = inputValue.trim();
    setInputValue("");
    if (!chatStarted) setChatStarted(true);
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setIsLoading(true);

    const nameMatch = userMessage.match(/(?:my name is|i'm|i am|call me)\s+([A-Z][a-z]+)/i);
    if (nameMatch) {
      const mem = { ...userMemory, name: nameMatch[1] };
      saveUserMemory(mem);
      setUserMemory(mem);
    }

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage,
        mood: selectedMood?.id || "heard",
        session_id: sessionId,
        user_memory: userMemory,
        chat_history: messages,
      });
      setMessages(prev => [...prev, { role: "ai", content: response.data.response }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: "ai", content: "I'm here with you. Could you share that again?" }]);
    }
    setIsLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleGoBack = () => {
    setChatStarted(false);
    setMessages([]);
    setSelectedMood(null);
    setCurrentBg(BACKGROUND_IMAGES.primary);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
  };

  return (
    <div className="relative h-screen w-screen overflow-hidden bg-black" data-testid="mysukoon-app">
      
      {/* Cinematic Background */}
      <div 
        className="cinematic-bg"
        style={{ backgroundImage: `url(${currentBg})` }}
        data-testid="background-image"
      />
      
      {/* Rain Animation Layer */}
      <div className="rain-layer" />
      
      {/* Floating Particles */}
      <div className="particles-layer">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>
      
      {/* Ambient Glow */}
      <div className="ambient-glow" />
      
      {/* Film Grain Texture */}
      <div className="film-grain" />
      
      {/* Dark Overlay - 60% for readability */}
      <div className="absolute inset-0 bg-black/60 z-[3]" />

      {/* Main Layout */}
      <div className="relative z-10 flex flex-col h-screen">
        
        {/* HEADER - Top Center */}
        <header className="pt-8 pb-6 text-center" data-testid="brand-header">
          {/* Logo: MY SUK(Moon)ON with proper spacing */}
          <h1 className="brand-logo flex items-center justify-center tracking-[0.2em]">
            <span>MY SUK</span>
            <Moon size={30} className="text-white/90 mx-1" strokeWidth={1.5} />
            <span>ON</span>
          </h1>
          <p className="brand-tagline mt-2">Your late-night friend</p>
          
          {/* Audio Controls - Below tagline */}
          <div className="flex items-center justify-center gap-3 mt-4" data-testid="audio-controls">
            <button
              onClick={() => { setIsMuted(!isMuted); if (isMuted && audioRef.current) audioRef.current.play().catch(() => {}); }}
              className="text-white/40 hover:text-white/70 transition-colors"
              data-testid="mute-button"
            >
              {isMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
            </button>
            <input
              type="range" min="0" max="1" step="0.01"
              value={isMuted ? 0 : volume}
              onChange={(e) => { setVolume(parseFloat(e.target.value)); setIsMuted(false); if (audioRef.current) audioRef.current.play().catch(() => {}); }}
              className="w-20 h-1 bg-white/20 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white/60"
              data-testid="volume-slider"
            />
          </div>
        </header>

        {/* CHAT AREA - Middle, 60% width, Glassmorphism */}
        <main className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
          
          {/* Welcome Text - Before Chat */}
          <AnimatePresence>
            {!chatStarted && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center mb-10"
              >
                <h2 className="welcome-title">Welcome home.</h2>
                <p className="welcome-subtitle mt-3">The world is quiet now. How are you feeling tonight?</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chat Container - Glassmorphism */}
          <AnimatePresence>
            {chatStarted && messages.length > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                className="w-full max-w-3xl flex-1 mb-6"
              >
                <div className="chat-glass h-full max-h-[40vh] overflow-y-auto p-6" data-testid="chat-container">
                  <div className="space-y-6">
                    {messages.map((msg, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`${msg.role === "user" ? "text-right" : "text-left"}`}
                        data-testid={`chat-message-${msg.role}-${idx}`}
                      >
                        <div className={`inline-block max-w-[85%] px-5 py-4 rounded-2xl ${
                          msg.role === "user" 
                            ? "user-bubble" 
                            : "ai-bubble"
                        }`}>
                          <p className={msg.role === "ai" ? "ai-text" : "user-text"}>
                            {msg.content}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                    
                    {isLoading && (
                      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-left">
                        <div className="ai-bubble inline-block px-5 py-4 rounded-2xl">
                          <div className="flex gap-2">
                            <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                            <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                            <span className="w-2 h-2 bg-white/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                          </div>
                        </div>
                      </motion.div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </div>
                
                {/* Back button */}
                <button
                  onClick={handleGoBack}
                  className="block mx-auto mt-3 text-white/40 hover:text-white/60 text-sm transition-colors"
                >
                  ← Start new conversation
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Chat Bar + Mood Cards - Centered */}
          <div className="w-full max-w-3xl">
            {/* Cylinder Input Bar - Center of Screen */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="max-w-2xl mx-auto mb-6"
              data-testid="chat-bar-container"
            >
              <div className="input-cylinder" data-testid="chat-bar">
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Tell me what's on your mind..."
                  className="input-field"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isLoading}
                  className="send-btn"
                  data-testid="send-button"
                >
                  <Send size={20} />
                </button>
              </div>
            </motion.div>

            {/* 5 Mood Cards - Below Chat Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex justify-center gap-3"
              data-testid="mood-selector"
            >
              {Object.values(MOODS).map((mood) => {
                const Icon = mood.icon;
                const isSelected = selectedMood?.id === mood.id;
                return (
                  <button
                    key={mood.id}
                    onClick={() => handleMoodSelect(mood)}
                    className={`mood-card ${isSelected ? "mood-card-active" : ""}`}
                    data-testid={`mood-button-${mood.id}`}
                  >
                    <Icon size={20} className="mood-icon" style={{ color: mood.iconColor }} />
                    <span className="mood-label">{mood.label}</span>
                  </button>
                );
              })}
            </motion.div>
          </div>
        </main>
      </div>

      {/* Audio */}
      <audio ref={audioRef} src="https://www.soundjay.com/nature/rain-01.mp3" loop preload="auto" data-testid="audio-player" />
    </div>
  );
}

export default App;
